/**
 * Лента диалога ассистента (US-10 FR-1.2): реплики пользователя и
 * ассистента, индикатор «печатает», карточки результата во всю ширину,
 * провенанс ответа и кнопки-действия под ним.
 */
import { AlertTriangle, ArrowRight, FileText, Sparkles, User } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ResultBlock } from './ResultBlocks';
import type { ChatEntry } from './useAssistantChat';
import { Button } from '../ui/button';
import { cn } from '../../lib/cn';
import type {
  AssistantAnswer,
  AssistantMode,
  NoteFormat,
} from '../../types/assistant';

const Bubble = ({
  role,
  children,
}: {
  role: 'user' | 'assistant';
  children: ReactNode;
}) => (
  <div
    className={cn(
      'mb-4 flex gap-2.5 animate-fade-up',
      role === 'user' && 'flex-row-reverse',
    )}
  >
    <span
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-lg',
        role === 'user'
          ? 'bg-brand-100 text-brand-700'
          : 'bg-brand-500 text-text-inverted',
      )}
      aria-hidden="true"
    >
      {role === 'user' ? (
        <User className="size-4" />
      ) : (
        <Sparkles className="size-4" />
      )}
    </span>
    <div
      className={cn(
        'max-w-[19rem] rounded-card border px-3.5 py-3 text-sm shadow-card',
        role === 'user'
          ? 'border-brand-500 bg-brand-500 text-text-inverted'
          : 'border-surface-border bg-surface text-text',
      )}
    >
      {children}
    </div>
  </div>
);

const TypingIndicator = () => {
  const { t } = useTranslation();
  return (
    <Bubble role="assistant">
      <span className="sr-only">{t('assistant.typing')}</span>
      <span className="flex gap-1 py-1" aria-hidden="true">
        <span className="size-1.5 animate-typing-dot rounded-full bg-brand-100" />
        <span className="size-1.5 animate-typing-dot rounded-full bg-brand-100 [animation-delay:0.2s]" />
        <span className="size-1.5 animate-typing-dot rounded-full bg-brand-100 [animation-delay:0.4s]" />
      </span>
    </Bubble>
  );
};

/** Провенанс: источник и период значения (FR-3.3, FR-4.3). */
const SourceNote = ({
  title,
  detail,
  period,
}: {
  title: string;
  detail: string | null;
  period: string | null;
}) => (
  <div
    className="mb-4 ml-10 flex gap-2.5 rounded-card border border-brand-100 bg-brand-50 px-3 py-2.5 text-xs text-brand-700"
    data-testid="assistant-source"
  >
    <FileText className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
    <div>
      <b className="block">{title}</b>
      {[detail, period].filter((part) => part !== null).join(' · ')}
    </div>
  </div>
);

/** Служебное сообщение: сервис недоступен / нет доступа к территории. */
const ErrorNote = ({ code }: { code: 'unavailable' | 'forbidden' }) => {
  const { t } = useTranslation();
  return (
    <div
      className="mb-4 flex gap-2.5 rounded-card border border-status-warning/40 bg-status-warning/10 px-3 py-2.5 text-xs text-text"
      role="status"
      data-testid={`assistant-error-${code}`}
    >
      <AlertTriangle
        className="mt-0.5 size-3.5 shrink-0 text-status-warning"
        aria-hidden="true"
      />
      <p>{t(`assistant.errors.${code}`)}</p>
    </div>
  );
};

interface AnswerEntryProps {
  answer: AssistantAnswer;
  onPrompt: (text: string, mode: AssistantMode | null) => void;
  onNavigate: (href: string) => void;
  onExport: (answer: AssistantAnswer, format: NoteFormat) => void;
  exporting: NoteFormat | null;
}

const AnswerEntry = ({
  answer,
  onPrompt,
  onNavigate,
  onExport,
  exporting,
}: AnswerEntryProps) => (
  <>
    {answer.text !== '' && (
      <Bubble role="assistant">
        <p className="whitespace-pre-line">{answer.text}</p>
      </Bubble>
    )}
    {answer.blocks.map((block, index) => (
      <ResultBlock
        // Блоки одного ответа приходят фиксированным списком — порядок и есть ключ.
        key={index}
        block={block}
        onExport={(format) => onExport(answer, format)}
        exporting={exporting}
      />
    ))}
    {/* Источников несколько: ответ по данным сводит показатели из разных
        разделов, ответ Гида — из разных документов. Показывать один значило бы
        спрятать остальные и сделать ответ непроверяемым (FR-3.3, FR-4.3). */}
    {answer.sources.map((source, index) => (
      <SourceNote
        key={`${source.title}-${source.detail ?? ''}-${index}`}
        title={source.title}
        detail={source.detail}
        period={source.period}
      />
    ))}
    {answer.actions.length > 0 && (
      <div className="mb-4 ml-10 flex flex-wrap gap-2">
        {answer.actions.map((action) => {
          if (action.kind === 'export') {
            return (
              <Button
                key={`${action.kind}-${action.noteId}-${action.format}`}
                type="button"
                variant="outline"
                size="sm"
                disabled={exporting !== null}
                onClick={() => onExport(answer, action.format)}
              >
                {action.label}
              </Button>
            );
          }
          if (action.kind === 'navigate') {
            return (
              <Button
                key={`${action.kind}-${action.href}`}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onNavigate(action.href)}
              >
                {action.label}
                <ArrowRight className="size-3.5" />
              </Button>
            );
          }
          return (
            <Button
              key={`${action.kind}-${action.prompt}`}
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onPrompt(action.prompt, action.mode)}
            >
              {action.label}
            </Button>
          );
        })}
      </div>
    )}
  </>
);

interface ChatStreamProps {
  entries: ChatEntry[];
  pending: boolean;
  onPrompt: (text: string, mode: AssistantMode | null) => void;
  onNavigate: (href: string) => void;
  onExport: (answer: AssistantAnswer, format: NoteFormat) => void;
  exporting: NoteFormat | null;
}

export const ChatStream = ({
  entries,
  pending,
  onPrompt,
  onNavigate,
  onExport,
  exporting,
}: ChatStreamProps) => {
  const { t } = useTranslation();
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [entries, pending]);

  return (
    <div
      className="min-h-0 flex-1 overflow-y-auto p-4"
      data-testid="assistant-stream"
    >
      {/* Приветствие — статичная подпись интерфейса, а не ответ модели. */}
      <Bubble role="assistant">
        <p>{t('assistant.greeting')}</p>
      </Bubble>

      {entries.map((entry) => {
        switch (entry.kind) {
          case 'user':
            return (
              <Bubble key={entry.id} role="user">
                <p className="whitespace-pre-line">{entry.text}</p>
              </Bubble>
            );
          case 'answer':
            return (
              <AnswerEntry
                key={entry.id}
                answer={entry.answer}
                onPrompt={onPrompt}
                onNavigate={onNavigate}
                onExport={onExport}
                exporting={exporting}
              />
            );
          case 'error':
            return <ErrorNote key={entry.id} code={entry.code} />;
          default:
            return null;
        }
      })}

      {pending && <TypingIndicator />}
      <div ref={endRef} />
    </div>
  );
};
